import { type ReactElement, useEffect, useState } from 'react';
import translations from '../../../translations';
import type { Environment } from '../../../types/environment';
import { addDays, addYears } from '../../../utils/date';
import Loader from '../../loader/Loader';
import { ENVIRONMENT_RELEASE_FILTER_DATETIME_OPTIONS } from './constants';
import { useEnvironments } from './EnvironmentsProvider';
import Release from './Release';
import ReleasesListFilters from './ReleasesListFilters';

type Props = {
    environmentId: string | null;
    passedEnvironments: Environment[] | null;
    isActive: boolean;
};

const ReleasesList = ({ environmentId, passedEnvironments, isActive }: Props) => {
    const { getReleases, releasesLoading, releases, environmentsLoading, environments } =
        useEnvironments();
    const [dateTimeOption, setDateTimeOption] = useState('');
    const [fromDateFilter, setFromDateFilter] = useState<Date | null>(addDays(new Date(), -30));
    const [environmentFilter, setEnvironmentFilter] = useState(environmentId);
    const [searchTermFilter, setSearchTermFilter] = useState('');

    const reload = async (
        dateTimeFilter: Date | null,
        environmentIdFilter: string | null,
        termFilter: string,
    ) => {
        const filter = {
            environmentId: environmentIdFilter !== '' ? environmentIdFilter : null,
            fromDateTimeOffset: dateTimeFilter ?? null,
            searchTerm: termFilter,
        };

        await getReleases(filter);
    };

    // biome-ignore lint/correctness/useExhaustiveDependencies: Requires dedicated refactor to add all dependencies
    useEffect(() => {
        if (isActive) {
            reload(fromDateFilter, environmentFilter, searchTermFilter);
        }
    }, [isActive]);

    const onDateTimeFilterChange = (value: string) => {
        setDateTimeOption(value);
        let fromDate: Date | null = null;
        switch (value) {
            case ENVIRONMENT_RELEASE_FILTER_DATETIME_OPTIONS.all: {
                fromDate = null;
                break;
            }
            case ENVIRONMENT_RELEASE_FILTER_DATETIME_OPTIONS.lastYear: {
                fromDate = addYears(new Date(), -1);
                break;
            }
            case ENVIRONMENT_RELEASE_FILTER_DATETIME_OPTIONS.lastThirtyDays: {
                fromDate = addDays(new Date(), -30);
                break;
            }
            case ENVIRONMENT_RELEASE_FILTER_DATETIME_OPTIONS.lastSevenDays: {
                fromDate = addDays(new Date(), -7);
                break;
            }
            case ENVIRONMENT_RELEASE_FILTER_DATETIME_OPTIONS.lastTwentyFourHours: {
                fromDate = addDays(new Date(), -1);
                break;
            }
        }
        setFromDateFilter(fromDate);
        reload(fromDate, environmentFilter, searchTermFilter);
    };

    const onEnvironmentFilterChange = (value: string) => {
        setEnvironmentFilter(value);
        reload(fromDateFilter, value, searchTermFilter);
    };

    const onSearchTermFilterChange = (value: string) => {
        setSearchTermFilter(value);
        reload(fromDateFilter, environmentFilter, value);
    };

    let content: ReactElement | ReactElement[] | null = null;
    if (releasesLoading) {
        content = <Loader message="Loading releases" />;
    } else if (releases.length === 0) {
        content = <h5>{translations.ENVIRONMENT_no_releases}</h5>;
    } else if (!environmentsLoading) {
        content = releases.map((release) => (
            <Release
                release={release}
                key={release.id}
                reload={() => {
                    reload(fromDateFilter, environmentFilter, searchTermFilter);
                }}
                environmentId={environmentFilter}
                showRollback={release.canRollback}
                passedEnvironments={passedEnvironments ?? environments}
            />
        ));
    }

    return (
        <>
            <ReleasesListFilters
                environments={
                    environmentId
                        ? null
                        : passedEnvironments && passedEnvironments?.length > 0
                          ? passedEnvironments
                          : environments
                }
                onDateTimeFilterChange={onDateTimeFilterChange}
                dateTimeOption={dateTimeOption}
                onEnvironmentFilterChange={onEnvironmentFilterChange}
                environmentFilter={environmentFilter}
                onSearchTermFilterChange={onSearchTermFilterChange}
                searchTermFilter={searchTermFilter}
            />
            <div
                className={
                    environmentId ? 'expandable-area-window-large' : 'expandable-area-window'
                }
            >
                {content}
            </div>
        </>
    );
};

export default ReleasesList;
